import { Icon } from '@iconify/react'
import {
  BreadcrumbItem,
  Breadcrumbs,
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@nextui-org/react'
import { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { Link, useNavigate } from 'react-router-dom'
import api from 'src/lib/api'
const inActiveButtonClass = 'bg-transparent text-default-400 pl-5 pr-5'
const activeButtonClass = 'text-black-500 font-medium bg-white rounded-md  pl-5 pr-5'

function ClientPipeline({ pipeline, getPipelines }: any) {
  const [section, setSection] = useState<'In Pipeline' | 'Assigned' | 'Rejected'>('In Pipeline')
  const assigned = useMemo(
    () => pipeline.tenders?.filter((item: any) => !item.inPipeLine && !item.rejected),
    [pipeline.tenders],
  )
  const inPipeline = useMemo(
    () => pipeline.tenders?.filter((item: any) => item.inPipeLine && !item.rejected),
    [pipeline.tenders],
  )
  const rejectedTenders = useMemo(() => pipeline.tenders?.filter((item: any) => item.rejected), [pipeline.tenders])
  return (
    <div className="flex gap-2 flex-col pb-4">
      <div className="flex flex-row justify-between">
        <div className="flex flex-row gap-2">
          <Button
            variant="light"
            onPress={() => {
              const url = `${document.location.origin}/reports/${pipeline?.user?.uuid}`
              navigator.clipboard.writeText(url)
              toast.success('Link copied to clipboard')
            }}
          >
            <Icon icon={'lucide:copy'} />
          </Button>
          <div className="flex flex-col pb-2">
            <span className="font-bold text-sm text-default-500">
              {pipeline?.user?.firstname} {pipeline?.user?.lastname}
            </span>
            <span className="font-medium text-xs text-default-400">{pipeline?.user?.email}</span>
          </div>
        </div>
        <div className={'p-1 rounded-xl w-fit bg-default-100 h-fit'}>
          {['In Pipeline', 'Assigned', 'Rejected'].map((item) => (
            <Button
              key={item}
              size={'sm'}
              className={section.toLowerCase() === item.toLowerCase() ? activeButtonClass : inActiveButtonClass}
              onPress={() => {
                setSection(item)
              }}
            >
              {item} (
              {item === 'In Pipeline'
                ? inPipeline.length
                : item === 'Assigned'
                ? assigned.length
                : rejectedTenders.length}
              )
            </Button>
          ))}
        </div>
      </div>
      {/** !TODO: add tenders pagination here. */}
      <div>
        <Table
          classNames={{
            wrapper: 'p-0 shadow-none',
          }}
          aria-label="Table of public tenders"
        >
          <TableHeader>
            <TableColumn>Project description</TableColumn>
            <TableColumn>Notice number</TableColumn>
            <TableColumn>Status</TableColumn>
            <TableColumn>Actions</TableColumn>
          </TableHeader>
          <TableBody>
            {(section === 'Assigned' ? assigned : section === 'Rejected' ? rejectedTenders : inPipeline).map(
              (myTender: any, index: number) => {
                const tender = myTender.tender
                return (
                  <TableRow key={`${section}_${index}`}>
                    <TableCell className="max-w-[350px]">
                      <div className="flex flex-row gap-2">
                        {section === 'Rejected' && (
                          <Popover placement="bottom" showArrow={true}>
                            <PopoverTrigger>
                              <Button variant="light">
                                <Icon icon="lucide:message-square-warning" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent>
                              <div className="px-1 py-2">
                                <div className="text-tiny">{myTender.reason}</div>
                              </div>
                            </PopoverContent>
                          </Popover>
                        )}
                        <div
                          className="flex flex-col"
                          onClick={() => {
                            navigate(`/admin/pipeline/${myTender.uuid}`)
                          }}
                        >
                          <span className="text-black text-xs font-bold cursor-pointer hover:underline">
                            {tender.title}
                          </span>
                          <span className="text-default-400 text-xs font-medium">Buyer {tender.buyer}</span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Link to={tender.url} className="text-xs font-bold">
                        {tender.publicationNumber}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <span>{myTender.phase}</span>
                    </TableCell>
                    <TableCell>
                      <span>
                        <Button
                          variant="light"
                          onPress={async () => {
                            await api.post(`/admin/pipeline/${myTender.uuid}/remove`)
                            toast.success('Tender removed from pipeline')
                            getPipelines()
                          }}
                        >
                          <Icon icon="lucide:unlink" />
                        </Button>
                      </span>
                    </TableCell>
                  </TableRow>
                )
              },
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
export default function Pipelines() {
  const [pipelines, setPipelines] = useState<any>({})
  const navigate = useNavigate()

  const getPipelines = async () => {
    const res = await api.get('/admin/pipelines')
    setPipelines(res.data)
  }
  useEffect(() => {
    getPipelines()
  }, [])
  return (
    <div className="flex flex-col w-full max-w-screen-xl h-screen p-3">
      <Breadcrumbs className={'pl-2 pt-4'}>
        <BreadcrumbItem onClick={() => navigate('/')}>
          <Icon icon={'lucide:home'} /> Home
        </BreadcrumbItem>
        <BreadcrumbItem onClick={() => navigate(`/admin/pipelines`)}>Pipelines</BreadcrumbItem>
      </Breadcrumbs>
      <div className="flex w-full pt-8 pb-6 px-2 2xl:px-8">
        <header className="flex w-full justify-between">
          <div className="flex flex-col">
            <h1 className="text-xl font-bold text-default-900 lg:text-3xl">Project pipeline</h1>
            <p className="text-small lg:text-medium">Manage and track all your projects and documents in one place</p>
          </div>
        </header>
      </div>
      <div className="flex w-full flex-col gap-5 pb-2 max-w-full max-h-full px-2 2xl:px-8 overflow-y-auto">
        {Object.keys(pipelines).map((key, index) => (
          <ClientPipeline pipeline={pipelines[key]} key={`pipeline_${index}`} getPipelines={getPipelines} />
        ))}
      </div>
    </div>
  )
}
